import {useReducer} from 'preact/hooks';
import {useCallback, useEffect, useState} from 'react';
import uniq from 'ramda/src/uniq';
import Notifier from '@modules/ui/Notifier';
import debounce from 'lodash.debounce';
import {notEmptyOrNil} from '@ultradent/utilities/EmptyOrNil';
import {client} from '@core/GraphQLProvider';
import {UpdateOptInProductReorderReminder, UpdateProductOfferingLocations} from '@services/Customer/query.graphql';
import {toProductOfferingLocationsInput} from '@services/Customer/mutations';
import productOfferingsReducer from '@containers/FindADentistOptIn/reducer';
import {getChangesBy} from '@util/ArrayUtils';

const useProductOfferingLocations = ( {productOfferingLocations} ) => {
    const [snapshot, setSnapshot] = useState( '{}' );
    const [changeQueue, setChangeQueue] = useState( [] );
    const [isAllOn, setIsAllOn] = useState( productOfferingLocations.every( entity => entity.displayOnMap === true ) );
    const [state, dispatch] = useReducer( productOfferingsReducer, productOfferingLocations );

    useEffect( () => {
        clearChangeQueue();
    }, [] );

    useEffect( () => {
        if ( notEmptyOrNil( changeQueue ) ) {
            const prevState = JSON.parse( snapshot ).filter( entity => changeQueue.includes( entity.address.id ) );
            const changeSet = state.filter( entity => changeQueue.includes( entity.address.id ) );
            const targetChanges = getChangesBy( 'address.id', prevState, changeSet );

            saveChanges( targetChanges );
        }
    }, [changeQueue] );

    const saveChanges = useCallback( debounce( async changeSet => {
        const input = toProductOfferingLocationsInput( changeSet );
        clearChangeQueue();

        try {
            await client.mutate( {
                mutation: UpdateProductOfferingLocations,
                variables: {input}
            } );

            Notifier.notify( 'Successfully updated' );

            clearChangeQueue();
        }
        catch ( error ) {
            console.error( '[GraphQL:UpdateProductOfferingLocations]', error );
            Notifier.error( 'There was a problem while trying to update your product offerings. Please try again.' );
        }

    }, 2000 ), [] );

    const updateOptInProductReorderReminder = useCallback( ( optIn ) => {
        client.mutate( {
            mutation: UpdateOptInProductReorderReminder,
            variables: {input: optIn}
        } );
    }, [] );

    const updateProductOffering = useCallback( ( {addressId, productOfferingId, offered} ) => {
        queueUpdate( addressId );
        dispatch( {
            type: 'updateProductOffering',
            payload: {
                addressId,
                productOfferingId,
                offered
            }
        } );
    }, [] );

    const updateDisplayOnMap = useCallback( ( {addressId, displayOnMap} ) => {
        queueUpdate( addressId );
        dispatch( {
            type: 'updateDisplayOnMap',
            payload: {
                addressId,
                displayOnMap
            }
        } );
    }, [] );

    const toggleAllLocations = useCallback( () => {
        dispatch( {
            type: 'toggleAllLocations',
            payload: {displayOnMap: !isAllOn}
        } );
        setIsAllOn( state => !state );
        setChangeQueue( state.map( entity => entity.address.id ) );
    }, [state, isAllOn] );

    function queueUpdate ( addressId ) {
        setChangeQueue( state => uniq( [...state, addressId] ) );
    }

    function clearChangeQueue () {
        setSnapshot( JSON.stringify( state ) );
        setChangeQueue( [] );
    }

    return {
        productOfferingLocations: state,
        allLocationsOn: isAllOn,
        onOptInProductReorderReminder: updateOptInProductReorderReminder,
        onUpdateProductOffering: updateProductOffering,
        onUpdateDisplayOnMap: updateDisplayOnMap,
        onToggleAllLocations: toggleAllLocations
    };
};

export default useProductOfferingLocations;
